import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Button } from "../../Components/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../Components/Card";
import { apiGet } from "../../DataService/apiClient";
import { AddCourseWaitlistEntry, CreateCourse, DeleteCourseWaitlistEntry, GetAllCoursesAdmin, GetCourseWaitlist, UpdateCourse } from "../../DataService/CourseService";
import { CourseProductCardDto, GetProductCardByCourseId, SyncProductCardStripe } from "../../DataService/ProductCardService";
import { MembershipPlanService } from "../../DataService/MembershipPlanService";
import { AdminIncludedBookingUsageResponse } from "../../Types/AdminOrderTypes";
import { COURSE_CATEGORY_OPTIONS, COURSE_STATUS_OPTIONS, Course, CourseStatus, CourseVersionDto, CourseWaitlistEntry, DEFAULT_COURSE_CATEGORY, isStandaloneCourseCategory } from "../../Types/CourseTypes";
import { GROW_BASIC_PLAN, GROW_PREMIUM_PLAN } from "../../Types/ClientOfferTypes";
import { MembershipPlanDto, MembershipPlanVersionDto } from "../../Types/MembershipPlanTypes";
import { AdminIcon } from "../../Icons/AdminIcon";
import "./AdminPrograms.css";

type AdminProgramsView = "overview" | "memberships" | "rot" | "sessions" | "history";

const ADMIN_PROGRAM_TABS: { id: AdminProgramsView; label: string; icon: keyof typeof AdminIcon }[] = [
  { id: "overview", label: "Översikt", icon: "Graph" },
  { id: "memberships", label: "Medlemskap", icon: "OpenBook" },
  { id: "rot", label: "ROT", icon: "UserGroup" },
  { id: "sessions", label: "Samtal", icon: "SimpleCalendar" },
  { id: "history", label: "Historik", icon: "Clock" },
];

const normalizeProgramCategory = (category?: string | null) => {
  const normalized = String(category ?? "").trim().toLowerCase();
  if (normalized === "marketplace") return "marketplace";
  if (normalized === "grow" || normalized === "membership" || normalized === "__system-membership") return "__system-membership";
  if (normalized === "rot" || normalized === "__system-rot") return "__system-rot";
  return DEFAULT_COURSE_CATEGORY;
};

const getProgramCategoryLabel = (category?: string | null) => {
  switch (normalizeProgramCategory(category)) {
    case "marketplace": return "Marketplace";
    case "__system-membership": return "GROW";
    case "__system-rot": return "ROT";
    default: return "Marketplace";
  }
};

const isRotCategory = (category?: string | null) => {
  const normalized = String(category ?? "").trim().toLowerCase();
  return normalized === "rot" || normalized === "__system-rot";
};

const createRotCohortDraft = (): Partial<Course> => ({
  title: "Ny ROT-kohort",
  description: "",
  category: "__system-rot",
  status: CourseStatus.Draft,
  price: 0,
  registrationFee: 2000,
  startDate: null,
  finalPaymentDeadline: null,
  acceptanceResponseDays: 3,
  seatLimit: 12,
  waitlistEnabled: true,
  includedCallsPerWeek: 1,
  prepModuleAvailableImmediately: true,
  rotFullyPaidOverride: false,
  isStandalone: false,
});

const updateStandaloneFromCategory = (category?: string | null) => isStandaloneCourseCategory(category ?? DEFAULT_COURSE_CATEGORY);

const formatDate = (value?: string | null) => {
  if (!value) return "Ej satt";
  return new Date(value).toLocaleDateString("sv-SE");
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "Ej satt";
  return new Date(value).toLocaleString("sv-SE");
};

const fmtNumber = (value?: number | null) => (value ?? 0).toLocaleString("sv-SE");

const adminIconMap = AdminIcon as Record<string, any>;

const Timeline = ({
  items,
  emptyLabel,
}: {
  items: Array<{ title: string; meta: string; note?: string }>;
  emptyLabel: string;
}) => {
  if (items.length === 0) {
    return <p className="admin-programs-empty">{emptyLabel}</p>;
  }

  return (
    <div className="admin-programs-timeline">
      {items.map((item) => (
        <div key={`${item.title}-${item.meta}`} className="admin-programs-timeline-item">
          <strong>{item.title}</strong>
          <span>{item.meta}</span>
          {item.note && <p>{item.note}</p>}
        </div>
      ))}
    </div>
  );
};

const SummaryCard = ({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: keyof typeof AdminIcon;
}) => {
  const Icon = adminIconMap[icon];
  return (
    <div className="admin-programs-summary-card">
      <div className="admin-programs-summary-icon">
        <Icon width={18} height={18} fill="hsl(var(--primary))" />
      </div>
      <p className="admin-programs-summary-label">{label}</p>
      <div className="admin-programs-summary-value-row">
        <span className="admin-programs-summary-value">{value}</span>
        {sub && <span className="admin-programs-summary-sub">{sub}</span>}
      </div>
    </div>
  );
};

const PlanPreviewCard = ({
  plan,
  versions,
  expanded,
  onToggle,
  onSave,
  onSync,
  onChange,
}: {
  plan: MembershipPlanDto;
  versions: MembershipPlanVersionDto[];
  expanded: boolean;
  onToggle: () => void;
  onSave: () => void;
  onSync: () => void;
  onChange: (patch: Partial<MembershipPlanDto>) => void;
}) => {
  return (
    <div className={`admin-programs-accordion ${expanded ? "is-expanded" : ""}`}>
      <button type="button" className="admin-programs-accordion-header" onClick={onToggle}>
        <div>
          <div className="admin-programs-chip-row">
            <span className="admin-programs-chip">ID: {plan.id}</span>
            <span className={`admin-programs-chip ${plan.isActive ? "is-active" : ""}`}>{plan.isActive ? "Aktiv" : "Inaktiv"}</span>
          </div>
          <h3>{plan.title}</h3>
          <p>{plan.monthlyPrice} kr/mån</p>
        </div>
        <div className="admin-programs-accordion-actions">
          <span className="admin-programs-accordion-price">{plan.monthlyPrice} kr</span>
          <span className="admin-programs-chevron">›</span>
        </div>
      </button>

      {expanded && (
        <div className="admin-programs-accordion-body">
          <div className="admin-programs-field-grid">
            <label>
              <span>Nyckel</span>
              <input value={plan.key} onChange={(e) => onChange({ key: e.target.value })} />
            </label>
            <label>
              <span>Titel</span>
              <input value={plan.title} onChange={(e) => onChange({ title: e.target.value })} />
            </label>
            <label>
              <span>Pris per månad</span>
              <input type="number" value={plan.monthlyPrice} onChange={(e) => onChange({ monthlyPrice: Number(e.target.value) })} />
            </label>
            <label>
              <span>Aktiv</span>
              <select value={plan.isActive ? "yes" : "no"} onChange={(e) => onChange({ isActive: e.target.value === "yes" })}>
                <option value="yes">Ja</option>
                <option value="no">Nej</option>
              </select>
            </label>
          </div>

          <label className="admin-programs-full-field">
            <span>Beskrivning</span>
            <textarea value={plan.description} onChange={(e) => onChange({ description: e.target.value })} />
          </label>

          <div className="admin-programs-field-grid">
            <label>
              <span>Stripe price id</span>
              <input value={plan.stripePriceId ?? ""} readOnly />
            </label>
          </div>

          <div className="admin-programs-actions">
            <Button text="Spara medlemskap" onClick={onSave} />
            <Button text="Synka Stripe" variant="secondary" onClick={onSync} />
          </div>

          <div className="admin-programs-history-block">
            <div className="admin-programs-block-header">
              <h4>Versionshistorik</h4>
              <p>Senaste ändringar som redan stöds av backend.</p>
            </div>
            <Timeline
              items={versions.map((version) => ({
                title: `v${version.versionNumber} · ${version.changeType}`,
                meta: formatDateTime(version.createdAtUtc),
                note: `${version.title} · ${version.monthlyPrice} ${version.currency}`,
              }))}
              emptyLabel="Inga versioner ännu."
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CohortPreviewCard = ({
  cohort,
  versions,
  waitlist,
  waitlistDraft,
  productCard,
  expanded,
  onToggle,
  onSave,
  onSync,
  onChange,
  onUpdateDraft,
  onAddWaitlist,
  onRemoveWaitlist,
  loadingWaitlists,
}: {
  cohort: Course;
  versions: CourseVersionDto[];
  waitlist: CourseWaitlistEntry[];
  waitlistDraft: { name: string; email: string };
  productCard?: CourseProductCardDto;
  expanded: boolean;
  onToggle: () => void;
  onSave: () => void;
  onSync: () => void;
  onChange: (patch: Partial<Course>) => void;
  onUpdateDraft: (patch: Partial<{ name: string; email: string }>) => void;
  onAddWaitlist: () => void;
  onRemoveWaitlist: (entryId: number) => void;
  loadingWaitlists: boolean;
}) => {
  const isStandaloneCourse = cohort.isStandalone;

  return (
    <div className={`admin-programs-accordion ${expanded ? "is-expanded" : ""}`}>
      <button type="button" className="admin-programs-accordion-header" onClick={onToggle}>
        <div>
          <div className="admin-programs-chip-row">
            <span className="admin-programs-chip">{cohort.status === CourseStatus.Published ? "Publicerad" : cohort.status === CourseStatus.Archived ? "Arkiverad" : "Utkast"}</span>
            <span className="admin-programs-chip">{cohort.waitlistEnabled ? "Väntelista på" : "Väntelista av"}</span>
          </div>
          <div className="admin-programs-chip-row" style={{ marginTop: 8 }}>
            <span className="admin-programs-chip admin-programs-chip--primary">{getProgramCategoryLabel(cohort.category)}</span>
          </div>
          <h3>{cohort.title}</h3>
          <p>{formatDate(cohort.startDate)} · {fmtNumber(cohort.seatLimit)} platser</p>
        </div>
        <div className="admin-programs-accordion-actions">
          <span className="admin-programs-accordion-price">{cohort.registrationFee ?? 0} kr</span>
          <span className="admin-programs-chevron">›</span>
        </div>
      </button>

      {expanded && (
        <div className="admin-programs-accordion-body">
          <div className="admin-programs-field-grid admin-programs-field-grid--three">
            <label>
              <span>Titel</span>
              <input value={cohort.title} onChange={(e) => onChange({ title: e.target.value })} />
            </label>
            <label>
              <span>Startdatum</span>
              <input type="date" value={cohort.startDate ?? ""} onChange={(e) => onChange({ startDate: e.target.value })} />
            </label>
            <label>
              <span>Slutbetalning senast</span>
              <input type="date" value={cohort.finalPaymentDeadline ?? ""} onChange={(e) => onChange({ finalPaymentDeadline: e.target.value })} />
            </label>
          </div>

          <label className="admin-programs-full-field">
            <span>Beskrivning</span>
            <textarea value={cohort.description} onChange={(e) => onChange({ description: e.target.value })} />
          </label>

          <div className="admin-programs-field-grid admin-programs-field-grid--three">
            <label>
              <span>Svarstid i dagar</span>
              <input type="number" min={1} value={cohort.acceptanceResponseDays ?? 3} onChange={(e) => onChange({ acceptanceResponseDays: Number(e.target.value) })} />
            </label>
            <label>
              <span>Anmälningsavgift</span>
              <input type="number" value={cohort.registrationFee ?? 0} onChange={(e) => onChange({ registrationFee: Number(e.target.value) })} />
            </label>
            <label>
              <span>Fullt pris</span>
              <input type="number" value={cohort.price ?? 0} onChange={(e) => onChange({ price: Number(e.target.value) })} />
            </label>
          </div>

          <div className="admin-programs-field-grid admin-programs-field-grid--three">
            <label>
              <span>Veckor</span>
              <input type="number" value={cohort.includedCallsPerWeek ?? 1} onChange={(e) => onChange({ includedCallsPerWeek: Number(e.target.value) })} />
            </label>
            <label>
              <span>Platser</span>
              <input type="number" value={cohort.seatLimit ?? 0} onChange={(e) => onChange({ seatLimit: Number(e.target.value) })} />
            </label>
            <label>
              <span>Status</span>
              <select value={cohort.status} onChange={(e) => onChange({ status: Number(e.target.value) as CourseStatus })}>
                {COURSE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="admin-programs-field-grid admin-programs-field-grid--three">
            <label>
              <span>Väntelista</span>
              <select value={cohort.waitlistEnabled ? "yes" : "no"} onChange={(e) => onChange({ waitlistEnabled: e.target.value === "yes" })}>
                <option value="yes">Aktiverad</option>
                <option value="no">Avstängd</option>
              </select>
            </label>
            <label>
              <span>Förberedande modul</span>
              <select value={cohort.prepModuleAvailableImmediately ? "yes" : "no"} onChange={(e) => onChange({ prepModuleAvailableImmediately: e.target.value === "yes" })}>
                <option value="yes">Direkt efter anmälan</option>
                <option value="no">Låst</option>
              </select>
            </label>
            <label>
              <span>ROT fullt betald</span>
              <select value={cohort.rotFullyPaidOverride ? "yes" : "no"} onChange={(e) => onChange({ rotFullyPaidOverride: e.target.value === "yes" })}>
                <option value="yes">Ja</option>
                <option value="no">Nej</option>
              </select>
            </label>
          </div>

          <div className="admin-programs-field-grid admin-programs-field-grid--two">
            <label>
              <span>Kategori</span>
              <select value={cohort.category ?? DEFAULT_COURSE_CATEGORY} onChange={(e) => onChange({ category: e.target.value, isStandalone: updateStandaloneFromCategory(e.target.value) })}>
                {COURSE_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {isStandaloneCourse ? (
              <label>
                <span>Stripe status</span>
                <input value={productCard?.stripeProductId ? "Synkad" : "Inte synkad"} readOnly />
              </label>
            ) : (
              <label>
                <span>Stripe status</span>
                <input value="Intern kohort" readOnly />
              </label>
            )}
          </div>

          {isStandaloneCourse ? (
            <div className="admin-programs-field-grid admin-programs-field-grid--two">
              <label>
                <span>Stripe price id</span>
                <input value={productCard?.stripePriceId ?? ""} readOnly />
              </label>
              <label>
                <span>Stripe product id</span>
                <input value={productCard?.stripeProductId ?? ""} readOnly />
              </label>
            </div>
          ) : (
            <p className="admin-programs-empty">Intern kohort utan produktkort.</p>
          )}

          <div className="admin-programs-actions">
            <Button text="Spara kohort" onClick={onSave} />
            <Button text="Synka Stripe" variant="secondary" onClick={onSync} disabled={cohort.id <= 0 || !isStandaloneCourse} />
          </div>

          <div className="admin-programs-split-grid">
            <div className="admin-programs-surface-panel">
              <div className="admin-programs-block-header">
                <h4>Versionshistorik</h4>
                <p>ROT-versioner som redan stöds av backend.</p>
              </div>
              <Timeline
                items={versions.map((version) => ({
                  title: `v${version.versionNumber} · ${version.changeType}`,
                  meta: formatDateTime(version.createdAtUtc),
                  note: `${version.title} · ${version.registrationFee ?? 0} kr i anmälningsavgift`,
                }))}
                emptyLabel="Inga versioner ännu."
              />
            </div>

            <div className="admin-programs-surface-panel">
              <div className="admin-programs-block-header">
                <h4>Väntelista</h4>
                <p>{waitlist.length} personer i kön</p>
              </div>

              {loadingWaitlists ? (
                <p className="admin-programs-empty">Laddar väntelista...</p>
              ) : (
                <>
                  <div className="admin-programs-field-grid admin-programs-field-grid--two">
                    <label>
                      <span>Namn</span>
                      <input value={waitlistDraft.name} onChange={(e) => onUpdateDraft({ name: e.target.value })} />
                    </label>
                    <label>
                      <span>E-post</span>
                      <input value={waitlistDraft.email} onChange={(e) => onUpdateDraft({ email: e.target.value })} />
                    </label>
                  </div>

                  <div className="admin-programs-actions">
                    <Button text="Lägg till på väntelista" onClick={onAddWaitlist} />
                  </div>

                  <div className="admin-programs-waitlist-list">
                    {(waitlist ?? []).length === 0 ? (
                      <p className="admin-programs-empty">Ingen väntelista ännu.</p>
                    ) : (
                      waitlist.map((entry) => (
                        <div key={entry.id} className="admin-programs-waitlist-row">
                          <div>
                            <strong>{entry.name}</strong>
                            <p>{entry.email}</p>
                          </div>
                          <div>
                            <span>{formatDate(entry.createdAt)}</span>
                            <button type="button" onClick={() => onRemoveWaitlist(entry.id)}>Ta bort</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminPrograms = () => {
  const [activeView, setActiveView] = useState<AdminProgramsView>("overview");
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlanDto[]>([]);
  const [membershipPlanVersions, setMembershipPlanVersions] = useState<Record<string, MembershipPlanVersionDto[]>>({});
  const [rotCohorts, setRotCohorts] = useState<Course[]>([]);
  const [waitlistByCourseId, setWaitlistByCourseId] = useState<Record<number, CourseWaitlistEntry[]>>({});
  const [waitlistDrafts, setWaitlistDrafts] = useState<Record<number, { name: string; email: string }>>({});
  const [productCardsByCourseId, setProductCardsByCourseId] = useState<Record<number, CourseProductCardDto>>({});
  const [courseVersionsById, setCourseVersionsById] = useState<Record<number, CourseVersionDto[]>>({});
  const [usage, setUsage] = useState<AdminIncludedBookingUsageResponse | null>(null);
  const [loadingCohorts, setLoadingCohorts] = useState(true);
  const [loadingMembershipPlans, setLoadingMembershipPlans] = useState(true);
  const [loadingWaitlists, setLoadingWaitlists] = useState(false);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);
  const [expandedCohortId, setExpandedCohortId] = useState<number | null>(null);

  const cohortCount = useMemo(() => rotCohorts.length, [rotCohorts]);
  const waitlistCount = useMemo(() => Object.values(waitlistByCourseId).reduce((sum, entries) => sum + entries.length, 0), [waitlistByCourseId]);
  const usageRows = usage?.rows ?? [];
  const premiumRows = usageRows.filter((row) => row.source === "grow-premium");
  const rotRows = usageRows.filter((row) => row.source === "rot-program");
  const premiumUsed = premiumRows.reduce((sum, row) => sum + row.used, 0);
  const premiumRemaining = premiumRows.reduce((sum, row) => sum + row.remaining, 0);
  const rotUsed = rotRows.reduce((sum, row) => sum + row.used, 0);
  const rotRemaining = rotRows.reduce((sum, row) => sum + row.remaining, 0);

  const loadWaitlists = useCallback(async (courses: Course[]) => {
    if (courses.length === 0) {
      setWaitlistByCourseId({});
      setLoadingWaitlists(false);
      return;
    }

    setLoadingWaitlists(true);
    try {
      const entries = await Promise.all(courses.map(async (course) => [course.id, await GetCourseWaitlist(course.id)] as const));
      setWaitlistByCourseId(Object.fromEntries(entries));
    } catch (error) {
      console.error("Failed to load waitlists", error);
      toast.error("Kunde inte hämta väntelistorna");
    } finally {
      setLoadingWaitlists(false);
    }
  }, []);

  const loadCohorts = useCallback(async () => {
    const allCourses = await GetAllCoursesAdmin();
      const rotCourses = (allCourses ?? [])
      .filter((course) => isRotCategory(course.category))
      .map((course) => ({ ...course, category: normalizeProgramCategory(course.category), isStandalone: updateStandaloneFromCategory(course.category) }));

    setRotCohorts(rotCourses);

    const productCardEntries = await Promise.all(rotCourses
      .filter((course) => course.isStandalone)
      .map(async (course) => {
        try {
          const card = await GetProductCardByCourseId(course.id);
          return card ? [course.id, card] as const : null;
        } catch {
          return null;
        }
      }));

    setProductCardsByCourseId(Object.fromEntries(productCardEntries.filter(Boolean) as Array<readonly [number, CourseProductCardDto]>));

    const versionEntries = await Promise.all(rotCourses.map(async (course) => {
      try {
        const versions = await apiGet<CourseVersionDto[]>(`/courses/${course.id}/versions`);
        return [course.id, versions] as const;
      } catch {
        return [course.id, []] as const;
      }
    }));

    setCourseVersionsById(Object.fromEntries(versionEntries));

    setWaitlistDrafts((prev) => {
      const next = { ...prev };
      rotCourses.forEach((course) => {
        if (!next[course.id]) {
          next[course.id] = { name: "", email: "" };
        }
      });
      return next;
    });

    await loadWaitlists(rotCourses);
    setLoadingCohorts(false);
    return rotCourses;
  }, [loadWaitlists]);

  const loadMembershipPlans = useCallback(async () => {
    setLoadingMembershipPlans(true);
    try {
      const plans = await MembershipPlanService.getAdmin();
      setMembershipPlans(plans);

      const versionEntries = await Promise.all(plans.map(async (plan) => {
        try {
          const versions = await MembershipPlanService.getVersions(plan.id);
          return [plan.id, versions] as const;
        } catch {
          return [plan.id, []] as const;
        }
      }));

      setMembershipPlanVersions(Object.fromEntries(versionEntries));
    } catch (error) {
      console.error("Failed to load membership plans", error);
      setMembershipPlans([
        {
          id: "basic-fallback",
          key: "basic",
          title: GROW_BASIC_PLAN.label,
          description: GROW_BASIC_PLAN.description,
          monthlyPrice: GROW_BASIC_PLAN.pricePerMonth,
          currency: "sek",
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        {
          id: "premium-fallback",
          key: "premium",
          title: GROW_PREMIUM_PLAN.label,
          description: GROW_PREMIUM_PLAN.description,
          monthlyPrice: GROW_PREMIUM_PLAN.pricePerMonth,
          currency: "sek",
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ]);
      setMembershipPlanVersions({});
    } finally {
      setLoadingMembershipPlans(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        await Promise.all([
          loadCohorts(),
          loadMembershipPlans(),
          (async () => {
            try {
              const result = await apiGet<AdminIncludedBookingUsageResponse>("/AdminOrders/included-booking-usage");
              setUsage(result);
            } catch (err) {
              console.error("Failed to load included booking usage", err);
            }
          })(),
        ]);
      } catch (err) {
        console.error("Failed to load ROT programs", err);
        setLoadingCohorts(false);
      }
    };

    load();
  }, [loadCohorts, loadMembershipPlans]);

  const updateCohort = (index: number, patch: Partial<Course>) => {
    setRotCohorts((prev) => prev.map((cohort, i) => {
      if (i !== index) return cohort;
      const nextCategory = normalizeProgramCategory(patch.category ?? cohort.category);
      return {
        ...cohort,
        ...patch,
        category: nextCategory,
        isStandalone: updateStandaloneFromCategory(nextCategory),
      };
    }));
  };

  const updateMembershipPlan = (index: number, patch: Partial<MembershipPlanDto>) => {
    setMembershipPlans((prev) => prev.map((plan, i) => (i === index ? { ...plan, ...patch } : plan)));
  };

  const updateWaitlistDraft = (courseId: number, patch: Partial<{ name: string; email: string }>) => {
    setWaitlistDrafts((prev) => ({
      ...prev,
      [courseId]: {
        name: prev[courseId]?.name ?? "",
        email: prev[courseId]?.email ?? "",
        ...patch,
      },
    }));
  };

  const addWaitlistEntry = async (courseId: number) => {
    const draft = waitlistDrafts[courseId];
    if (!draft?.name.trim() || !draft.email.trim()) {
      toast.error("Fyll i namn och e-post för väntelistan");
      return;
    }

    try {
      const saved = await AddCourseWaitlistEntry(courseId, draft);
      setWaitlistByCourseId((prev) => ({
        ...prev,
        [courseId]: [...(prev[courseId] ?? []), saved],
      }));
      setWaitlistDrafts((prev) => ({ ...prev, [courseId]: { name: "", email: "" } }));
      toast.success("Väntelistepost sparad");
    } catch (error) {
      console.error("Failed to add waitlist entry", error);
      toast.error("Kunde inte lägga till posten i väntelistan");
    }
  };

  const removeWaitlistEntry = async (courseId: number, entryId: number) => {
    try {
      await DeleteCourseWaitlistEntry(entryId);
      setWaitlistByCourseId((prev) => ({
        ...prev,
        [courseId]: (prev[courseId] ?? []).filter((entry) => entry.id !== entryId),
      }));
      toast.success("Väntelistepost borttagen");
    } catch (error) {
      console.error("Failed to remove waitlist entry", error);
      toast.error("Kunde inte ta bort posten från väntelistan");
    }
  };

  const saveCohort = async (index: number) => {
    const cohort = rotCohorts[index];
    try {
      const normalizedCategory = normalizeProgramCategory(cohort.category);
      const payload = { ...cohort, category: normalizedCategory, isStandalone: updateStandaloneFromCategory(normalizedCategory) };
      await UpdateCourse(payload);
      toast.success(`ROT-kohort sparad: ${cohort.title}`);
      await loadCohorts();
    } catch (err) {
      console.error("Failed to save ROT cohort", err);
      toast.error("Kunde inte spara ROT-kohorten");
    }
  };

  const syncRotStripe = async (courseId: number) => {
    if (courseId <= 0) {
      toast.warning("Spara ROT-kohorten först innan du synkar Stripe.");
      return;
    }

    const cohort = rotCohorts.find((item) => item.id === courseId);
    if (!cohort?.isStandalone) {
      toast.warning("Interna ROT-kohorter har inget produktkort att synka.");
      return;
    }

    try {
      const synced = await SyncProductCardStripe(courseId);
      if (!synced) {
        toast.error("Kunde inte synka ROT-kohorten till Stripe");
        return;
      }

      setProductCardsByCourseId((prev) => ({
        ...prev,
        [courseId]: synced,
      }));

      toast.success("ROT-kohort synkad med Stripe");
      await loadCohorts();
    } catch (err) {
      console.error("Failed to sync ROT cohort to Stripe", err);
      toast.error("Kunde inte synka ROT-kohorten till Stripe");
    }
  };

  const addCohort = async () => {
    try {
      await CreateCourse(createRotCohortDraft());
      toast.success("ROT-kohort skapad");
      await loadCohorts();
      setActiveView("rot");
    } catch (err) {
      console.error("Failed to create ROT cohort", err);
      toast.error("Kunde inte skapa ROT-kohort");
    }
  };

  const savePrograms = async () => {
    try {
      await Promise.all(rotCohorts.map((cohort) => {
        const category = normalizeProgramCategory(cohort.category);
        return UpdateCourse({ ...cohort, category, isStandalone: updateStandaloneFromCategory(category) });
      }));
      toast.success("ROT-inställningar sparade");
    } catch (err) {
      console.error("Failed to save program settings", err);
      toast.error("Kunde inte spara ROT-konfigurationen");
    }
  };

  const saveMembershipPlan = async (index: number) => {
    const plan = membershipPlans[index];
    try {
      const updated = await MembershipPlanService.update(plan.id, {
        key: plan.key,
        title: plan.title,
        description: plan.description,
        monthlyPrice: plan.monthlyPrice,
        currency: plan.currency,
        isActive: plan.isActive,
      });
      setMembershipPlans((prev) => prev.map((item, i) => (i === index ? updated : item)));
      const versions = await MembershipPlanService.getVersions(updated.id);
      setMembershipPlanVersions((prev) => ({ ...prev, [updated.id]: versions }));
      toast.success(`${updated.title} sparad`);
    } catch (error) {
      console.error("Failed to save membership plan", error);
      toast.error("Kunde inte spara medlemskapet");
    }
  };

  const syncMembershipPlan = async (index: number) => {
    const plan = membershipPlans[index];
    try {
      const synced = await MembershipPlanService.syncStripe(plan.id);
      setMembershipPlans((prev) => prev.map((item, i) => (i === index ? synced : item)));
      const versions = await MembershipPlanService.getVersions(synced.id);
      setMembershipPlanVersions((prev) => ({ ...prev, [synced.id]: versions }));
      toast.success(`${synced.title} synkad med Stripe`);
    } catch (error) {
      console.error("Failed to sync membership plan", error);
      toast.error("Kunde inte synka med Stripe");
    }
  };

  const membershipHistoryItems = membershipPlans.flatMap((plan) =>
    (membershipPlanVersions[plan.id] ?? []).slice(0, 3).map((version) => ({
      title: `${plan.title} · v${version.versionNumber}`,
      meta: formatDateTime(version.createdAtUtc),
      note: `${version.changeType} · ${version.monthlyPrice} ${version.currency}`,
    }))
  );

  const rotHistoryItems = rotCohorts.flatMap((cohort) =>
    (courseVersionsById[cohort.id] ?? []).slice(0, 3).map((version) => ({
      title: `${cohort.title} · v${version.versionNumber}`,
      meta: formatDateTime(version.createdAtUtc),
      note: `${version.changeType} · ${version.registrationFee ?? 0} kr`,
    }))
  );

  const overviewCards = (
    <div className="admin-programs-overview-grid">
      <SummaryCard label="ROT-kohorter" value={fmtNumber(cohortCount)} sub="aktiva" icon="UserGroup" />
      <SummaryCard label="Väntelisteposter" value={fmtNumber(waitlistCount)} sub="totalt" icon="Clock" />
      <SummaryCard label="GROW Premium" value={fmtNumber(premiumUsed)} sub={`/${fmtNumber(premiumRemaining)} kvar`} icon="OpenBook" />
      <SummaryCard label="ROT samtal" value={fmtNumber(rotUsed)} sub={`/${fmtNumber(rotRemaining)} kvar`} icon="SimpleCalendar" />
    </div>
  );

  return (
    <div className="admin-programs-page admin-page-shell">
      <header className="admin-programs-hero">
        <div>
          <p className="admin-programs-kicker">Programcenter</p>
          <h1>Program & medlemskap</h1>
          <p>En kontrollvy för GROW, ROT och inkluderade samtal.</p>
        </div>

        <div className="admin-programs-hero-actions">
          <Button text="Spara allt" onClick={savePrograms} />
          <Button text="Ny ROT-kohort" variant="secondary" onClick={addCohort} />
        </div>
      </header>

      <nav className="admin-programs-tabbar">
        {ADMIN_PROGRAM_TABS.map((tab) => {
          const Icon = adminIconMap[tab.icon];
          return (
            <button
              key={tab.id}
              type="button"
              className={`admin-programs-tab ${activeView === tab.id ? "is-active" : ""}`}
              onClick={() => setActiveView(tab.id)}
            >
              <Icon width={14} height={14} fill="currentColor" />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {overviewCards}

      <main className="admin-programs-main">
        {activeView === "overview" && (
          <section className="admin-programs-overview-panels">
            <Card className="admin-programs-card" disableHover>
              <CardHeader>
                <CardTitle>Medlemskap</CardTitle>
                <CardDescription>Snabb överblick över Basic och Premium.</CardDescription>
              </CardHeader>
              <CardContent className="admin-programs-stack">
                {loadingMembershipPlans ? (
                  <p className="admin-programs-empty">Laddar medlemskapsplaner...</p>
                ) : (
                  membershipPlans.slice(0, 2).map((plan) => (
                    <div key={plan.id} className="admin-programs-mini-card">
                      <strong>{plan.title}</strong>
                      <p>{plan.monthlyPrice} kr/mån</p>
                      <span>{plan.isActive ? "Aktiv" : "Inaktiv"}</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="admin-programs-card" disableHover>
              <CardHeader>
                <CardTitle>ROT-kohorter</CardTitle>
                <CardDescription>Snabb överblick över aktiva kohorter och väntelistor.</CardDescription>
              </CardHeader>
              <CardContent className="admin-programs-stack">
                {loadingCohorts ? (
                  <p className="admin-programs-empty">Laddar ROT-kohorter...</p>
                ) : (
                  rotCohorts.slice(0, 3).map((cohort) => (
                    <div key={cohort.id} className="admin-programs-mini-card">
                      <strong>{cohort.title}</strong>
                      <p>{formatDate(cohort.startDate)} · {fmtNumber(cohort.seatLimit)} platser</p>
                      <span>{(waitlistByCourseId[cohort.id] ?? []).length} i kö</span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="admin-programs-card" disableHover>
              <CardHeader>
                <CardTitle>Inkluderade samtal</CardTitle>
                <CardDescription>Aktuell användning för GROW Premium och ROT.</CardDescription>
              </CardHeader>
              <CardContent className="admin-programs-stack">
                <div className="admin-programs-mini-card">
                  <strong>GROW Premium</strong>
                  <p>{premiumUsed} använda / {premiumRemaining} kvar</p>
                </div>
                <div className="admin-programs-mini-card">
                  <strong>ROT</strong>
                  <p>{rotUsed} använda / {rotRemaining} kvar</p>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {activeView === "memberships" && (
          <section className="admin-programs-list">
            {loadingMembershipPlans ? (
              <Card className="admin-programs-card" disableHover>
                <CardContent>
                  <p className="admin-programs-empty">Laddar medlemskapsplaner...</p>
                </CardContent>
              </Card>
            ) : (
              membershipPlans.map((plan, index) => (
                <PlanPreviewCard
                  key={plan.id}
                  plan={plan}
                  versions={membershipPlanVersions[plan.id] ?? []}
                  expanded={expandedPlanId === plan.id}
                  onToggle={() => setExpandedPlanId(expandedPlanId === plan.id ? null : plan.id)}
                  onSave={() => void saveMembershipPlan(index)}
                  onSync={() => void syncMembershipPlan(index)}
                  onChange={(patch) => updateMembershipPlan(index, patch)}
                />
              ))
            )}
          </section>
        )}

        {activeView === "rot" && (
          <section className="admin-programs-list">
            {loadingCohorts ? (
              <Card className="admin-programs-card" disableHover>
                <CardContent>
                  <p className="admin-programs-empty">Laddar ROT-kohorter...</p>
                </CardContent>
              </Card>
            ) : (
              rotCohorts.map((cohort, index) => (
                <CohortPreviewCard
                  key={cohort.id}
                  cohort={cohort}
                  versions={courseVersionsById[cohort.id] ?? []}
                  waitlist={waitlistByCourseId[cohort.id] ?? []}
                  waitlistDraft={waitlistDrafts[cohort.id] ?? { name: "", email: "" }}
                  productCard={productCardsByCourseId[cohort.id]}
                  expanded={expandedCohortId === cohort.id}
                  onToggle={() => setExpandedCohortId(expandedCohortId === cohort.id ? null : cohort.id)}
                  onSave={() => void saveCohort(index)}
                  onSync={() => void syncRotStripe(cohort.id)}
                  onChange={(patch) => updateCohort(index, patch)}
                  onUpdateDraft={(patch) => updateWaitlistDraft(cohort.id, patch)}
                  onAddWaitlist={() => void addWaitlistEntry(cohort.id)}
                  onRemoveWaitlist={(entryId) => void removeWaitlistEntry(cohort.id, entryId)}
                  loadingWaitlists={loadingWaitlists}
                />
              ))
            )}
          </section>
        )}

        {activeView === "sessions" && (
          <section className="admin-programs-sessions">
            <Card className="admin-programs-card" disableHover>
              <CardHeader>
                <CardTitle>Inkluderade samtal</CardTitle>
                <CardDescription>Översikt över användning per kund och period.</CardDescription>
              </CardHeader>
              <CardContent className="admin-programs-stack">
                <div className="admin-programs-overview-grid admin-programs-overview-grid--compact">
                  <SummaryCard label="GROW Premium" value={fmtNumber(premiumUsed)} sub={`/${fmtNumber(premiumRemaining)} kvar`} icon="OpenBook" />
                  <SummaryCard label="ROT" value={fmtNumber(rotUsed)} sub={`/${fmtNumber(rotRemaining)} kvar`} icon="SimpleCalendar" />
                </div>

                {usageRows.length === 0 ? (
                  <p className="admin-programs-empty">Inga aktiva inkluderade samtal hittades.</p>
                ) : (
                  <div className="admin-programs-table">
                    <div className="admin-programs-table-row admin-programs-table-head">
                      <span>E-post</span>
                      <span>Typ</span>
                      <span>Period</span>
                      <span>Använt</span>
                      <span>Kvar</span>
                    </div>
                    {usageRows.map((row) => (
                      <div key={`${row.email}-${row.source}-${row.periodKey}`} className="admin-programs-table-row">
                        <span>{row.email}</span>
                        <span>{row.source === "grow-premium" ? "GROW Premium" : "ROT"}</span>
                        <span>{row.periodLabel}</span>
                        <span>{row.used} / {row.limit}</span>
                        <span>{row.remaining}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        )}

        {activeView === "history" && (
          <section className="admin-programs-history-grid">
            <Card className="admin-programs-card" disableHover>
              <CardHeader>
                <CardTitle>Medlemskapshistorik</CardTitle>
                <CardDescription>Senaste planändringar från backend-versioner.</CardDescription>
              </CardHeader>
              <CardContent>
                <Timeline items={membershipHistoryItems.slice(0, 8)} emptyLabel="Ingen medlemskapshistorik ännu." />
              </CardContent>
            </Card>

            <Card className="admin-programs-card" disableHover>
              <CardHeader>
                <CardTitle>ROT-historik</CardTitle>
                <CardDescription>Versioner och ändringar för kohorter.</CardDescription>
              </CardHeader>
              <CardContent>
                <Timeline items={rotHistoryItems.slice(0, 8)} emptyLabel="Ingen ROT-historik ännu." />
              </CardContent>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminPrograms;
